// Tipos de anotaciones
export type AnnotationType = 'highlight' | 'shapes' | 'text' | 'freehand' | 'move';

// Tipos de formas
export type ShapeType = 'rectangle' | 'oval';

// Tipos de líneas
export type LineType = 'solid' | 'dashed' | 'dotted';

// Configuración de estilo
export interface AnnotationStyle {
  color: string;
  thickness: number;
  fontSize: number;
  fontFamily: string;
  lineType: LineType;
}

// Estructura base para todas las anotaciones
export interface BaseAnnotation {
  id: string;
  type: AnnotationType;
  x: number;
  y: number;
  page: number;
  style: AnnotationStyle;
  createdAt: Date;
  createdBy: string;
}

// Anotación de resaltado
export interface HighlightAnnotation extends BaseAnnotation {
  type: 'highlight';
  width: number;
  height: number;
  opacity: number;
}

// Anotación de formas
export interface ShapeAnnotation extends BaseAnnotation {
  type: 'shapes';
  shapeType: ShapeType;
  width: number;
  height: number;
  endX?: number; // Para flechas y líneas
  endY?: number;
}

// Anotación de texto
export interface TextAnnotation extends BaseAnnotation {
  type: 'text';
  text: string;
  width?: number;
  height?: number;
}

// Anotación de mano libre
export interface FreehandAnnotation extends BaseAnnotation {
  type: 'freehand';
  points: Array<{ x: number; y: number }>;
  note?: string;
}

// Unión de todos los tipos de anotaciones
export type Annotation = 
  | HighlightAnnotation 
  | ShapeAnnotation 
  | TextAnnotation 
  | FreehandAnnotation;

// Estado del editor de anotaciones
export interface AnnotationEditorState {
  currentTool: AnnotationType | null;
  currentStyle: AnnotationStyle;
  annotations: Annotation[];
  selectedAnnotation: Annotation | null;
  isDrawing: boolean;
  canUndo: boolean;
}

// Acciones del editor
export type AnnotationAction = 
  | { type: 'SET_CURRENT_TOOL'; payload: AnnotationType | null }
  | { type: 'SET_CURRENT_STYLE'; payload: AnnotationStyle }
  | { type: 'ADD_ANNOTATION'; payload: Annotation }
  | { type: 'UPDATE_ANNOTATION'; payload: { id: string; updates: Partial<Annotation> } }
  | { type: 'DELETE_ANNOTATION'; payload: string }
  | { type: 'SELECT_ANNOTATION'; payload: Annotation | null }
  | { type: 'SET_DRAWING_STATE'; payload: boolean }
  | { type: 'UNDO' }
  | { type: 'CLEAR_ALL' };

// Definición del tipo de acción para el historial
export type AnnotationActionHistory =
  | { type: 'add'; annotation: Annotation }
  | { type: 'move'; id: string; from: { x: number; y: number }; to: { x: number; y: number } }
  | { type: 'edit'; id: string; from: string; to: string }
  | { type: 'delete'; annotation: Annotation };

// Configuración por defecto
export const DEFAULT_ANNOTATION_STYLE: AnnotationStyle = {
  color: '#FF0000',
  thickness: 3,
  fontSize: 16,
  fontFamily: 'Arial',
  lineType: 'solid',
};

// Configuraciones específicas por tipo
export const TOOL_DEFAULTS: Record<AnnotationType, Partial<AnnotationStyle>> = {
  highlight: {
    color: '#FFFF00',
    thickness: 3,
  },
  shapes: {
    color: '#FF0000',
    thickness: 2,
  },
  text: {
    color: '#000000',
    fontSize: 14,
    fontFamily: 'Arial',
  },
  freehand: {
    color: '#0000FF',
    thickness: 2,
  },
  move: {},
}; 